import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, PlayCircle, Save, X, Folder, Video as VideoIcon, ExternalLink, Info, Eye, MousePointerClick, BarChart3, CheckCircle2, Clock } from "lucide-react";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";

const EMPTY_VIDEO = {
  title: "", description: "", category_id: null,
  source_type: "youtube", youtube_id: "", video_url: "",
  thumbnail_url: "", duration_seconds: 0, order: 0,
  is_hero: false, is_active: true,
  ctas: [], resources: [],
  post_cta_title: "הצעד הבא שלך", post_cta_buttons: [],
};

function extractYouTubeId(input) {
  if (!input) return "";
  const s = input.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
  const m = s.match(/(?:v=|\/embed\/|youtu\.be\/|shorts\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : s;
}

export default function AdminVOD() {
  const [tab, setTab] = useState("videos");
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [analytics, setAnalytics] = useState({}); // { [video_id]: { views, completions, cta_clicks } }
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [analyticsFor, setAnalyticsFor] = useState(null); // video whose detail dialog is open

  const load = async () => {
    setLoading(true);
    try {
      const [c, v, a] = await Promise.all([
        api.get("/admin/video-categories"),
        api.get("/admin/videos"),
        api.get("/admin/videos/analytics/summary").catch(() => ({ data: {} })),
      ]);
      setCategories(c.data);
      setVideos(v.data);
      setAnalytics(a.data || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout title="VOD · אזור החברים">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5" data-testid="vod-tabs">
          <TabsTrigger value="videos" data-testid="tab-videos"><VideoIcon className="w-4 h-4 me-2" />סרטונים ({videos.length})</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories"><Folder className="w-4 h-4 me-2" />קטגוריות ({categories.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="videos">
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-stone-500">ניהול הספרייה של אזור החברים. הוסיפו לינק YouTube (URL מלא או ID של 11 תווים).</p>
            <Button onClick={() => setEditing({ ...EMPTY_VIDEO })} data-testid="new-video-btn" className="h-11 rounded-full bg-brand-charcoal hover:bg-stone-800 text-white">
              <Plus className="w-4 h-4 me-2" />סרטון חדש
            </Button>
          </div>
          <VideosList videos={videos} categories={categories} analytics={analytics} onEdit={setEditing} onShowAnalytics={setAnalyticsFor} onReload={load} />
        </TabsContent>

        <TabsContent value="categories">
          <CategoriesPanel categories={categories} onReload={load} />
        </TabsContent>
      </Tabs>

      <VideoEditorDialog
        video={editing}
        categories={categories}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <VideoAnalyticsDialog
        video={analyticsFor}
        onClose={() => setAnalyticsFor(null)}
      />
    </AdminLayout>
  );
}

function VideosList({ videos, categories, analytics, onEdit, onShowAnalytics, onReload }) {
  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const remove = async (v) => {
    if (!window.confirm(`למחוק את "${v.title}"?`)) return;
    try { await api.delete(`/admin/videos/${v.id}`); toast.success("נמחק"); onReload(); }
    catch { toast.error("שגיאה"); }
  };

  if (videos.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 border border-dashed border-stone-300 text-center" data-testid="videos-empty">
        <VideoIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" />
        <h3 className="font-[800] mb-2">אין סרטונים בספרייה</h3>
        <p className="text-sm text-stone-500">הוסיפו סרטון ראשון כדי להתחיל.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="videos-table">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 border-b border-stone-200 text-right">
          <tr>
            <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">כותרת</th>
            <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">קטגוריה</th>
            <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 text-center">אנליטיקה</th>
            <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 text-center">Hero</th>
            <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 text-center">פעיל</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {videos.map((v) => {
            const a = (analytics || {})[v.id] || { views: 0, completions: 0, cta_clicks: 0 };
            return (
              <tr key={v.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`video-row-${v.id}`}>
                <td className="px-4 py-3 font-bold">
                  <div className="flex items-center gap-3">
                    <img src={v.thumbnail_url} alt="" className="w-16 aspect-video object-cover rounded-md bg-stone-100" onError={(e)=>{e.currentTarget.style.opacity=0.3}} />
                    <div>
                      <div>{v.title}</div>
                      <div className="text-xs text-stone-400">{v.ctas?.length || 0} CTA · {v.resources?.length || 0} משאבים · <span className="ltr">{v.youtube_id || v.video_url?.slice(0,24) || "—"}</span></div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-stone-600">{catName(v.category_id)}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onShowAnalytics(v)}
                    className="flex items-center gap-1.5 mx-auto px-3 py-1.5 rounded-lg bg-stone-50 hover:bg-amber-50 hover:border-amber-300 border border-stone-200 transition-colors group whitespace-nowrap"
                    title={`${a.views} צפיות ייחודיות · ${a.cta_clicks} קליקים על CTA — לחץ לפירוט`}
                    data-testid={`video-analytics-${v.id}`}
                  >
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                      <Eye className="w-3.5 h-3.5" />
                      <span data-testid={`va-views-${v.id}`}>{a.views}</span>
                    </span>
                    <span className="text-stone-300 text-xs">·</span>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700">
                      <MousePointerClick className="w-3.5 h-3.5" />
                      <span data-testid={`va-clicks-${v.id}`}>{a.cta_clicks}</span>
                    </span>
                    <BarChart3 className="w-3.5 h-3.5 text-stone-400 group-hover:text-amber-600 transition-colors ms-1" />
                  </button>
                </td>
                <td className="px-4 py-3 text-center">{v.is_hero ? <span className="text-amber-600 font-bold">★</span> : "—"}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${v.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-200 text-stone-500"}`}>
                    {v.is_active ? "פעיל" : "כבוי"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <a href={`/community/watch/${v.id}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-600" title="פתח בתצוגה"><ExternalLink className="w-4 h-4" /></a>
                    <button onClick={() => onEdit(v)} data-testid={`video-edit-${v.id}`} className="p-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-brand-amber" title="עריכה"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => remove(v)} data-testid={`video-del-${v.id}`} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" title="מחיקה"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// ============================================================
// VIDEO ANALYTICS — DETAIL DIALOG
// ============================================================

function VideoAnalyticsDialog({ video, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!video?.id) { setData(null); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/videos/${video.id}/analytics`);
        setData(data);
      } catch (err) {
        toast.error("שגיאה בטעינת האנליטיקה");
      } finally { setLoading(false); }
    })();
  }, [video?.id]);

  if (!video) return null;

  const fmtDuration = (secs) => {
    if (!secs) return "0:00";
    const s = Math.floor(secs);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
    return h > 0 ? `${h}:${String(m).padStart(2,"0")}:${String(r).padStart(2,"0")}` : `${m}:${String(r).padStart(2,"0")}`;
  };
  const sourceLabels = { cta_overlay: "באנר על הפלייר", post_cta: "כפתור סיום", resource: "משאב נלווה", unknown: "לא ידוע" };

  return (
    <Dialog open={!!video} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="video-analytics-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            אנליטיקה — {video.title}
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="py-16 text-center text-stone-400">טוען…</div>}

        {data && !loading && (
          <div className="space-y-6 py-2">
            {/* KPI CARDS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard
                Icon={Eye} tone="emerald"
                label="צפיות ייחודיות"
                value={data.views.unique_viewers}
                sub={`${data.views.starts} התחלות כולל`}
                testid="kpi-views"
              />
              <KpiCard
                Icon={CheckCircle2} tone="sky"
                label="השלמות"
                value={data.views.completions}
                sub={`${data.views.completion_rate}% מהצופים סיימו`}
                testid="kpi-completions"
              />
              <KpiCard
                Icon={MousePointerClick} tone="amber"
                label="קליקים על CTA"
                value={data.cta_clicks.total}
                sub={`CTR ${data.cta_clicks.ctr_pct}% מצופים`}
                testid="kpi-cta-clicks"
              />
              <KpiCard
                Icon={Clock} tone="violet"
                label="זמן צפייה ממוצע"
                value={fmtDuration(data.views.avg_watch_seconds)}
                sub={`סה"כ ${fmtDuration(data.views.total_watch_seconds)}`}
                testid="kpi-avg-watch"
              />
            </div>

            {/* CTA breakdown by source */}
            {data.cta_clicks.total > 0 && (
              <div className="bg-stone-50 border border-stone-200 rounded-2xl p-4" data-testid="cta-by-source">
                <div className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">התפלגות קליקים לפי מיקום</div>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(data.cta_clicks.by_source).map(([k, n]) => (
                    <div key={k} className="bg-white rounded-xl border border-stone-200 p-3">
                      <div className="text-xs text-stone-500 mb-0.5">{sourceLabels[k] || k}</div>
                      <div className="text-lg font-black text-amber-700">{n}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top affiliates */}
            {data.cta_clicks.top_affiliates.length > 0 && (
              <div className="bg-stone-50 border border-stone-200 rounded-2xl p-4" data-testid="top-affiliates">
                <div className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">משווקים מובילים (Top 10)</div>
                <div className="space-y-1.5">
                  {data.cta_clicks.top_affiliates.map((a) => (
                    <div key={a.affiliate_id} className="flex items-center justify-between bg-white rounded-lg border border-stone-200 px-3 py-2 text-sm">
                      <span className="font-medium text-stone-800">{a.name || a.affiliate_id}</span>
                      <span className="text-amber-700 font-black">{a.clicks}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.views.unique_viewers === 0 && data.cta_clicks.total === 0 && (
              <div className="text-center py-8 text-stone-400 text-sm">
                אין עדיין נתונים לסרטון הזה. הנתונים יופיעו ברגע שחברי הקהילה יתחילו לצפות.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>סגור</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KpiCard({ Icon, label, value, sub, tone, testid }) {
  const tones = {
    emerald: { bg: "bg-emerald-50", ring: "border-emerald-200", text: "text-emerald-700", icon: "text-emerald-600" },
    sky:     { bg: "bg-sky-50",     ring: "border-sky-200",     text: "text-sky-700",     icon: "text-sky-600"     },
    amber:   { bg: "bg-amber-50",   ring: "border-amber-200",   text: "text-amber-700",   icon: "text-amber-600"   },
    violet:  { bg: "bg-violet-50",  ring: "border-violet-200",  text: "text-violet-700",  icon: "text-violet-600"  },
  };
  const t = tones[tone] || tones.amber;
  return (
    <div className={`${t.bg} border ${t.ring} rounded-2xl p-3.5`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${t.icon}`} />
        {label}
      </div>
      <div className={`text-2xl font-black ${t.text} leading-none mb-0.5`}>{value}</div>
      {sub && <div className="text-[11px] text-stone-500">{sub}</div>}
    </div>
  );
}

function CategoriesPanel({ categories, onReload }) {
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("שם חובה");
    try {
      if (editing.id) await api.put(`/admin/video-categories/${editing.id}`, editing);
      else await api.post("/admin/video-categories", editing);
      toast.success("נשמר");
      setEditing(null);
      onReload();
    } catch { toast.error("שגיאה"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`למחוק את "${c.name}"? סרטונים שנמצאים בה יעברו ל"כללי".`)) return;
    try { await api.delete(`/admin/video-categories/${c.id}`); toast.success("נמחק"); onReload(); }
    catch { toast.error("שגיאה"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-stone-500">קטגוריות מגדירות את ה-swimlanes בעמוד הבית של החברים.</p>
        <Button onClick={() => setEditing({ name: "", description: "", order: 0, is_active: true })} data-testid="new-cat-btn" className="h-11 rounded-full bg-brand-charcoal text-white">
          <Plus className="w-4 h-4 me-2" />קטגוריה חדשה
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 divide-y divide-stone-100">
        {categories.length === 0 && <div className="p-8 text-center text-sm text-stone-500">אין קטגוריות. הוסיפו לפחות אחת.</div>}
        {categories.map((c) => (
          <div key={c.id} className="flex items-center gap-4 px-4 py-3" data-testid={`cat-row-${c.id}`}>
            <Folder className="w-5 h-5 text-brand-amber flex-shrink-0" />
            <div className="flex-1">
              <div className="font-bold">{c.name}</div>
              {c.description && <div className="text-xs text-stone-500">{c.description}</div>}
            </div>
            <span className="text-xs text-stone-400 font-mono">order: {c.order}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-200 text-stone-500"}`}>
              {c.is_active ? "פעיל" : "כבוי"}
            </span>
            <button onClick={() => setEditing(c)} data-testid={`cat-edit-${c.id}`} className="p-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-brand-amber"><Edit3 className="w-4 h-4" /></button>
            <button onClick={() => remove(c)} data-testid={`cat-del-${c.id}`} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader><DialogTitle>{editing?.id ? "עריכת קטגוריה" : "קטגוריה חדשה"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div><Label className="text-sm font-bold mb-1.5 block">שם</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="cat-name-input" placeholder="מיינדסט מנצח" className="h-11" />
              </div>
              <div><Label className="text-sm font-bold mb-1.5 block">תיאור</Label>
                <Textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-sm font-bold mb-1.5 block">סדר תצוגה</Label>
                  <Input type="number" value={editing.order || 0} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 0, 10) })} data-testid="cat-order-input" className="h-11" />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Label className="text-sm font-bold">פעיל</Label>
                  <Switch checked={editing.is_active !== false} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>ביטול</Button>
            <Button onClick={save} data-testid="cat-save-btn" className="bg-brand-charcoal text-white"><Save className="w-4 h-4 me-2" />שמור</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VideoEditorDialog({ video, categories, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_VIDEO);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (video) setForm({ ...EMPTY_VIDEO, ...video });
  }, [video]);

  if (!video) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("כותרת חובה");
    let payload = { ...form };
    if (form.source_type === "direct") {
      if (!form.video_url?.trim()) return toast.error("URL של הסרטון חובה");
      if (!/^https?:\/\//.test(form.video_url.trim())) return toast.error("URL חייב להתחיל ב-http/https");
      payload.youtube_id = "";
    } else {
      const ytid = extractYouTubeId(form.youtube_id);
      if (!/^[a-zA-Z0-9_-]{11}$/.test(ytid)) return toast.error("YouTube ID לא תקין");
      payload = { ...payload, youtube_id: ytid, video_url: "" };
    }
    setSaving(true);
    try {
      if (form.id) await api.put(`/admin/videos/${form.id}`, payload);
      else await api.post("/admin/videos", payload);
      toast.success("נשמר");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה");
    } finally { setSaving(false); }
  };

  const updateCTA = (i, patch) => set("ctas", form.ctas.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const addCTA = () => set("ctas", [...form.ctas, { id: `cta_${Date.now()}`, start_time_seconds: 0, end_time_seconds: null, text: "", link_url: "", use_affiliate_link: true }]);
  const removeCTA = (i) => set("ctas", form.ctas.filter((_, idx) => idx !== i));

  const updateRes = (i, patch) => set("resources", form.resources.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRes = () => set("resources", [...form.resources, { id: `res_${Date.now()}`, title: "", url: "", icon: "calculator" }]);
  const removeRes = (i) => set("resources", form.resources.filter((_, idx) => idx !== i));

  const updateBtn = (i, patch) => set("post_cta_buttons", form.post_cta_buttons.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const addBtn = () => set("post_cta_buttons", [...form.post_cta_buttons, { text: "", link_url: "", use_affiliate_link: true }]);
  const removeBtn = (i) => set("post_cta_buttons", form.post_cta_buttons.filter((_, idx) => idx !== i));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{form.id ? "עריכת סרטון" : "סרטון חדש"}</DialogTitle></DialogHeader>
        <div className="space-y-5 py-2">
          {/* Basics */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-sm font-bold mb-1.5 block">כותרת</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="video-title-input" className="h-11" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-bold mb-1.5 block">תיאור</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="video-desc-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-bold mb-1.5 block">סוג מקור</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => set("source_type", "youtube")}
                  data-testid="source-yt-btn"
                  className={`px-4 py-3 rounded-xl border-2 text-sm font-bold transition-colors ${form.source_type !== "direct" ? "border-brand-amber bg-amber-50 text-brand-charcoal" : "border-stone-200 text-stone-500 hover:bg-stone-50"}`}
                >
                  YouTube (Unlisted)
                </button>
                <button
                  type="button"
                  onClick={() => set("source_type", "direct")}
                  data-testid="source-direct-btn"
                  className={`px-4 py-3 rounded-xl border-2 text-sm font-bold transition-colors ${form.source_type === "direct" ? "border-brand-amber bg-amber-50 text-brand-charcoal" : "border-stone-200 text-stone-500 hover:bg-stone-50"}`}
                >
                  קובץ ישיר (Native PiP)
                </button>
              </div>
              <p className="text-xs text-stone-500 mt-1.5 leading-relaxed">
                {form.source_type === "direct"
                  ? "URL ישיר של MP4/HLS. חבר/ת קהילה יוכל/תוכל להפעיל תמונה-בתוך-תמונה מובנה בדפדפן."
                  : "לינק YouTube (חייב Public/Unlisted). מכיוון ש-YouTube iframe לא תומך ב-PiP מובנה, נשתמש במיני-פלייר צף שיופיע בגלילה."}
              </p>
            </div>
            {form.source_type === "direct" ? (
              <div className="md:col-span-2">
                <Label className="text-sm font-bold mb-1.5 block">URL של הסרטון (MP4 / HLS)</Label>
                <Input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://cdn.example.com/videos/xyz.mp4" data-testid="video-url-input" className="h-11 ltr" />
              </div>
            ) : (
              <div>
                <Label className="text-sm font-bold mb-1.5 block">YouTube URL או ID</Label>
                <Input value={form.youtube_id} onChange={(e) => set("youtube_id", e.target.value)} placeholder="dQw4w9WgXcQ או https://youtu.be/..." data-testid="video-yt-input" className="h-11 ltr" />
              </div>
            )}
            <div>
              <Label className="text-sm font-bold mb-1.5 block">משך (שניות)</Label>
              <Input type="number" min={0} value={form.duration_seconds} onChange={(e) => set("duration_seconds", parseInt(e.target.value || 0, 10))} data-testid="video-dur-input" className="h-11" />
            </div>
            {form.source_type === "direct" && (
              <div className="md:col-span-2">
                <Label className="text-sm font-bold mb-1.5 block">תמונה ממוזערת (thumbnail URL)</Label>
                <Input value={form.thumbnail_url || ""} onChange={(e) => set("thumbnail_url", e.target.value)} placeholder="https://cdn.example.com/thumbs/xyz.jpg" className="h-11 ltr" />
              </div>
            )}
            <div>
              <Label className="text-sm font-bold mb-1.5 block">קטגוריה</Label>
              <Select value={form.category_id || "NONE"} onValueChange={(v) => set("category_id", v === "NONE" ? null : v)}>
                <SelectTrigger className="h-11" data-testid="video-cat-select"><SelectValue placeholder="ללא" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">ללא קטגוריה</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold mb-1.5 block">סדר בקטגוריה</Label>
              <Input type="number" value={form.order || 0} onChange={(e) => set("order", parseInt(e.target.value || 0, 10))} className="h-11" />
            </div>
            <div className="flex items-center gap-6 md:col-span-2 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={form.is_hero} onCheckedChange={(v) => set("is_hero", v)} data-testid="video-hero-switch" />
                <span className="text-sm font-bold">Hero (מוצג בראש עמוד הבית)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} />
                <span className="text-sm font-bold">פעיל</span>
              </label>
            </div>
          </div>

          {/* CTAs */}
          <Section title="CTA overlays (באנרים על הפלייר)" onAdd={addCTA}>
            {form.ctas.length === 0 && <EmptyRow>אין CTAs מוגדרים</EmptyRow>}
            {form.ctas.map((c, i) => {
              const useAff = c.use_affiliate_link !== false; // default true
              return (
                <div key={i} className="bg-stone-50 rounded-lg p-3 border border-stone-200 space-y-2" data-testid={`cta-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-2"><Label className="text-[10px]">התחלה (שניות)</Label><Input type="number" value={c.start_time_seconds} onChange={(e) => updateCTA(i, { start_time_seconds: parseInt(e.target.value || 0, 10) })} className="h-9" /></div>
                    <div className="col-span-2"><Label className="text-[10px]">סוף (שניות)</Label><Input type="number" value={c.end_time_seconds || ""} onChange={(e) => updateCTA(i, { end_time_seconds: e.target.value ? parseInt(e.target.value, 10) : null })} placeholder="+15" className="h-9" /></div>
                    <div className="col-span-4"><Label className="text-[10px]">טקסט</Label><Input value={c.text} onChange={(e) => updateCTA(i, { text: e.target.value })} placeholder="מוכנים למפות?" className="h-9" /></div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">לינק Fallback</Label>
                      <Input value={c.link_url} onChange={(e) => updateCTA(i, { link_url: e.target.value })} placeholder="/funnel/1" className="h-9 ltr" disabled={useAff} />
                    </div>
                    <div className="col-span-1 flex items-end"><button onClick={() => removeCTA(i)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center"><X className="w-4 h-4" /></button></div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer select-none pt-1" data-testid={`cta-use-affiliate-${i}`}>
                    <input
                      type="checkbox"
                      checked={useAff}
                      onChange={(e) => updateCTA(i, { use_affiliate_link: e.target.checked })}
                      className="w-4 h-4 rounded border-stone-300 text-brand-amber focus:ring-brand-amber"
                    />
                    <span>
                      <strong>השתמש בקישור המשווק של הצופה</strong>
                      <span className="text-stone-500 me-1">— ה-CTA יפנה אוטומטית ל-affiliate_url של המשווק שהביא את הליד (Funnel B). כשאין משווק, ייעשה שימוש בקישור ה-Fallback.</span>
                    </span>
                  </label>
                </div>
              );
            })}
          </Section>

          {/* Resources */}
          <Section title="משאבים נלווים (קישורים מתחת לפלייר)" onAdd={addRes}>
            {form.resources.length === 0 && <EmptyRow>אין משאבים</EmptyRow>}
            {form.resources.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 bg-stone-50 rounded-lg p-3 border border-stone-200" data-testid={`res-row-${i}`}>
                <div className="col-span-5"><Label className="text-[10px]">כותרת</Label><Input value={r.title} onChange={(e) => updateRes(i, { title: e.target.value })} placeholder="מחשבון מיפוי חובות" className="h-9" /></div>
                <div className="col-span-6"><Label className="text-[10px]">URL</Label><Input value={r.url} onChange={(e) => updateRes(i, { url: e.target.value })} placeholder="/funnel/3" className="h-9 ltr" /></div>
                <div className="col-span-1 flex items-end"><button onClick={() => removeRes(i)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center"><X className="w-4 h-4" /></button></div>
              </div>
            ))}
          </Section>

          {/* Post-video actions */}
          <Section title="אזור הצעות (אחרי סיום הסרטון)" onAdd={addBtn}>
            <div className="mb-2">
              <Label className="text-sm font-bold mb-1.5 block">כותרת</Label>
              <Input value={form.post_cta_title} onChange={(e) => set("post_cta_title", e.target.value)} placeholder="הצעד הבא שלך" className="h-11" data-testid="post-title-input" />
            </div>
            {form.post_cta_buttons.length === 0 && <EmptyRow>אין כפתורים</EmptyRow>}
            {form.post_cta_buttons.map((b, i) => {
              const useAff = b.use_affiliate_link !== false; // default true
              return (
                <div key={i} className="bg-stone-50 rounded-lg p-3 border border-stone-200 space-y-2" data-testid={`postbtn-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-5"><Label className="text-[10px]">טקסט הכפתור</Label><Input value={b.text} onChange={(e) => updateBtn(i, { text: e.target.value })} placeholder="לבדיקת זכאות" className="h-9" /></div>
                    <div className="col-span-6"><Label className="text-[10px]">URL Fallback</Label><Input value={b.link_url ?? b.url ?? ""} onChange={(e) => updateBtn(i, { link_url: e.target.value })} placeholder="/funnel/1" className="h-9 ltr" disabled={useAff} /></div>
                    <div className="col-span-1 flex items-end"><button onClick={() => removeBtn(i)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center"><X className="w-4 h-4" /></button></div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer select-none pt-1" data-testid={`postbtn-use-affiliate-${i}`}>
                    <input
                      type="checkbox"
                      checked={useAff}
                      onChange={(e) => updateBtn(i, { use_affiliate_link: e.target.checked })}
                      className="w-4 h-4 rounded border-stone-300 text-brand-amber focus:ring-brand-amber"
                    />
                    <span>
                      <strong>השתמש בקישור המשווק של הצופה</strong>
                      <span className="text-stone-500 me-1">— כשמסומן, הכפתור יפנה ל-affiliate_url של הליד. אחרת ישתמש ב-Fallback.</span>
                    </span>
                  </label>
                </div>
              );
            })}
          </Section>

          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <Info className="w-4 h-4 text-brand-amber mt-0.5 flex-shrink-0" />
            <p className="text-xs text-stone-700 leading-relaxed">
              טיפ: אם הסרטון פרטי — ודאו שהוא <strong>Unlisted</strong> ב-YouTube (לא Private). Public/Unlisted עובד; Private לא.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>ביטול</Button>
          <Button onClick={save} disabled={saving} data-testid="video-save-btn" className="bg-brand-charcoal text-white"><Save className="w-4 h-4 me-2" />{saving ? "שומר..." : "שמור"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, onAdd, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="font-[800] text-sm">{title}</h4>
        <Button size="sm" variant="outline" onClick={onAdd} className="rounded-full"><Plus className="w-3 h-3 me-1" />הוסף</Button>
      </div>
      {children}
    </div>
  );
}
function EmptyRow({ children }) {
  return <div className="text-xs text-stone-400 text-center py-3 border-2 border-dashed border-stone-200 rounded-lg">{children}</div>;
}
